import classNames from 'classnames';
import { MDText } from 'i18n-react';
import { MENTION_TEMPLATE_REGEXP, MENTION_ID_REGEXP, MENTION_DISPLAY_REGEXP } from './consts';
import { createTLoader } from './locale/TLoader';
import { defaultLocale } from './locale/consts';
import { escape as htmlEscape } from 'lodash';

/**
 * Object contains the element tags wrapping the mention
 * @type {{SPAN: string}}
 */
export const MENTION_ELEMENT = {
  SPAN: 'span'
};

/**
 * This functions searches for mentions based on a given template regex (using a default one). It then transforms it into an array of strings.
 * This output is rendered coupled with ParsedMention component which renders it accordingly.
 * @param text {string} - (Required) Text to be parsed.
 * @param entities {array} - (Required) A list of the project entities to get the name from. Needed in order to know if the mentioned entity no longer belongs to the project
 * @param currentUser {object} - (Required) Logged in user. Needed because we highlight the mentions if the mention is for the current user
 * @param customClass {object} - (optional) object with mention/ownMention/inactiveMention strings which are classes to add to each mention/own/inactive mentions, respectively.
 * @param regex {object} - (optional) An object containing two regexes by which to find mentions in the text.
 *        Object contains three keys: mentionRegex/idRegex/displayRegex. mentionRegex Defaults @("type":"*type*","id":"*id*","name":"*name*"}, while idRegex & displayRegex default to take those, respectively.
 *        Regex is executed on groups, i.e., "regex.exec(text)" and index 1 of the resulting array is taken.
 * @returns {array} - Returns an array of strings, where each mentions is a cutoff point to a new string
 */
const defaultClasses = { mention: '', ownMention: '', inactiveMention: '' };
const defaultRegex = {
  mentionRegex: MENTION_TEMPLATE_REGEXP,
  idRegex: MENTION_ID_REGEXP,
  displayRegex: MENTION_DISPLAY_REGEXP
};
export default (
  text,
  entities,
  currentUser,
  customClass,
  regex,
  localeArg = defaultLocale,
  inactiveUserText,
  { escapeHtml = false } = {}
) => {
  if (!text) return [];
  if (!entities) return [text];
  if (!currentUser) return [text];

  const escape = escapeHtml ? htmlEscape : str => str;

  const T = createTLoader({
    componentName: 'parseMentions',
    customTLoader: ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
  })(localeArg);

  customClass = { ...defaultClasses, ...customClass };
  regex = { ...defaultRegex, ...regex };
  const textParts = [];
  let result = text;
  let found = result.match(regex.mentionRegex);
  while (found) {
    const template = found[0];
    const mentionedId = regex.idRegex.exec(template)[1];
    const mentionedDisplayname = regex.displayRegex.exec(template)[1];
    regex.idRegex.lastIndex = 0;
    regex.displayRegex.lastIndex = 0;
    const targetIsActive = (entities || []).filter(
      entity => entity.id === mentionedId && entity.status !== 'deleted'
    )[0];
    const classes = classNames('ParsedMention__mention', customClass.mention, {
      'ParsedMention__mention--own': currentUser.id === mentionedId,
      'ParsedMention__mention--inactive': !targetIsActive,
      [customClass.inactiveMention]: !targetIsActive,
      [customClass.ownMention]: currentUser.id === mentionedId
    });

    textParts.push(escape(result.substring(0, found.index)));
    textParts.push(
      template.replace(
        template,
        `<${MENTION_ELEMENT.SPAN} class="${classes}">@${escape(mentionedDisplayname)}${
          !targetIsActive
            ? ` ${inactiveUserText || T.translate('MENTION_FORMER_USER_ALONGSIDE_NAME')}`
            : ''
        }</${MENTION_ELEMENT.SPAN}>`
      )
    );

    result = result.substring(found.index + template.length);
    found = result.match(regex.mentionRegex);
    regex.mentionRegex.lastIndex = 0;
  }

  textParts.push(escape(result));

  return textParts.filter(_ => _);
};
